import { useEffect, useState } from 'react';

import { Table } from 'components-antd';
import styles from './styles.module.scss';
import { Skeleton, SpinProps } from 'antd';
import { Icons } from 'pages/Workshop/Icons';
import classNames from 'classnames';
import { DraggableRowsTable } from '../DraggableRowsTable';
import SkeletonTable from '../SkeletonTable/SkeletonTable';

interface CollapsibleTableProps {
  title: string;
  defaultCollapse?: Boolean;
  loading?: boolean | SpinProps | undefined;
  dataSource: any;
  onRow?: any;
  columns: any[];
  tableClassName?: string;
  disableCollapse?: boolean;
  showCollapseIcon?: boolean;
  rowClassName?: any;
  collapsibleClassName?: string;
  isDraggableTable?: boolean;
  onChange?: () => void;
  showSkeletonTable?: boolean;
  customDragHandleUpdate?: (x, y, list) => void;
  customDraggingAllowedCondition?: (x, y) => boolean;
}

export const CollapsibleTable = ({
  title,
  defaultCollapse = false,
  loading = false,
  dataSource,
  onRow = null,
  columns,
  tableClassName,
  disableCollapse = false,
  showCollapseIcon = false,
  rowClassName = '',
  collapsibleClassName = '',
  isDraggableTable = false,
  onChange,
  showSkeletonTable = false,
  customDragHandleUpdate,
  customDraggingAllowedCondition,
}: CollapsibleTableProps) => {
  const [collapsed, setIsCollapsed] = useState(defaultCollapse);

  useEffect(() => {
    setIsCollapsed(defaultCollapse);
  }, [defaultCollapse]);

  if ((!dataSource || dataSource?.length === 0) && !loading) return null;

  function onClick() {
    if (disableCollapse) return;
    setIsCollapsed(!collapsed);
  }

  const renderTable = () => {
    return (
      <Table
        className={tableClassName}
        bordered={false}
        columns={columns}
        dataSource={dataSource.map((item, index) => ({
          ...item,
          key: item.Id || item.id || index,
        }))} // Insert unique 'key' using task ID or fallback to index
        pagination={false}
        onRow={onRow}
        rowClassName={rowClassName}
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) => {
            if (!record?.children) return null;
            return expanded ? (
              <Icons
                variant={Icons.COLLAPSE}
                className={styles.expandableIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  onExpand(record, e);
                }}
              />
            ) : (
              <Icons
                variant={Icons.EXPAND}
                className={styles.expandableIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  onExpand(record, e);
                }}
              />
            );
          },
        }}
        {...(onChange && { onChange })}
        {...(!showSkeletonTable && { loading })}
      />
    );
  };

  return (
    <div className={classNames(styles.collapsibleTable, collapsibleClassName)}>
      {title && (
        <p className={styles.tableTitle} onClick={onClick}>
          {title} {collapsed && <span className={styles.countText}>({dataSource?.length})</span>}
          {/* {!disableCollapse && showCollapseIcon && (
          <span>
            <DownOutlined
              className={classNames(styles.collapseIcon, { [styles.iconRotate]: !collapsed })}
            />
          </span>
        )} */}
        </p>
      )}
      {!collapsed &&
        (isDraggableTable ? (
          <DraggableRowsTable
            data={dataSource?.sort((a, b) => a.Order - b.Order)}
            className={tableClassName}
            columns={columns}
            onRow={onRow}
            customDragHandleUpdate={customDragHandleUpdate}
            customDraggingAllowedCondition={customDraggingAllowedCondition}
          />
        ) : showSkeletonTable ? (
          <SkeletonTable
            className={styles.workshopSkeletonTable}
            active={loading as boolean}
            loading={loading as boolean}
            columns={columns}
          >
            {renderTable()}
          </SkeletonTable>
        ) : (
          renderTable()
        ))}
    </div>
  );
};
